/**
 * ExperienceTimeline.js
 * Place in: src/components/ExperienceTimeline.js
 *
 * Logo files expected in: src/assets/icons/timeline/
 *   nci.svg, atos.svg, aws.svg, surecom.svg, tudublin.svg
 */

import React, { useState } from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiMapPin,
  FiCalendar,
  FiArrowRight,
} from "react-icons/fi";
import NciLogo from "../assets/icons/timeline/nci.svg";
import AtosLogo from "../assets/icons/timeline/atos.svg";
import AwsLogo from "../assets/icons/timeline/aws.svg";
import SurecomLogo from "../assets/icons/timeline/surecom.svg";
import TuDublinLogo from "../assets/icons/timeline/tudublin.svg";
import "../styles/ExperienceTimeline.css";

/* ── Data ─────────────────────────────────────────────────────── */
const TIMELINE = [
  {
    id: "msc",
    type: "edu",
    title: "MSc Cybersecurity",
    org: "National College of Ireland",
    logo: NciLogo,
    location: "Dublin",
    period: "Sept 2025 – Present",
    tag: "MSc",
    preview:
      "Advanced programme covering offensive security, cloud architecture, AI/ML security, and cyber governance.",
    bullets: [
      "Network Security & Penetration Testing",
      "Malware Analysis & Secure Web Development",
      "Cloud Architecture & Security",
      "AI/ML in Cybersecurity",
      "Cryptography & Blockchain",
      "Business Resilience & Incident Management",
      "Data Governance, Ethics & Sustainability",
    ],
  },
  {
    id: "atos",
    type: "work",
    title: "IT Support Analyst II",
    org: "Atos",
    logo: AtosLogo,
    location: "Dundrum, Dublin",
    period: "May 2025 – Apr 2026",
    tag: "Security",
    preview:
      "Security triage and incident response across M365 and Azure AD environments.",
    bullets: [
      "Triaged security signals across M365 & Azure AD for credential misuse",
      "Investigated anomalous PowerShell activity and endpoint alerts",
      "Coordinated incident response between IT operations and engineering",
    ],
  },
  {
    id: "aws",
    type: "work",
    title: "IT Hardware Technician",
    org: "Amazon Web Services",
    logo: AwsLogo,
    location: "Tallaght, Dublin",
    period: "Feb 2024 – Apr 2025",
    tag: "Cloud",
    preview:
      "Maintained secure cloud infrastructure hardware in a high-availability data centre environment.",
    bullets: [
      "Maintained cloud infrastructure hardware and access controls",
      "Performed firmware updates and vulnerability mitigation",
      "Ensured high-availability per global compliance standards",
    ],
  },
  {
    id: "surecom",
    type: "work",
    title: "IT Security Support Analyst",
    org: "Surecom",
    logo: SurecomLogo,
    location: "Rathcoole, Dublin",
    period: "Jan 2022 – Jan 2023",
    tag: "Security",
    preview:
      "Vulnerability scanning, traffic analysis, and ISO 27001 audit support.",
    bullets: [
      "Conducted vulnerability scans using Nessus & Nmap",
      "Analysed traffic via Wireshark to validate IoCs",
      "Supported ISO 27001 audits and GDPR compliance",
    ],
  },
  {
    id: "bsc",
    type: "edu",
    title: "BSc Computer Science",
    org: "TU Dublin",
    logo: TuDublinLogo,
    location: "Dublin",
    period: "Aug 2019 – May 2023",
    tag: "BSc",
    preview:
      "Four-year programme spanning programming, systems, networking, cloud, and advanced security.",
    bullets: [
      "OOP (Java, Python), Algorithms, Systems Software",
      "Operating Systems, Microprocessors, Architecture",
      "Networking 1–3 & Network Programming",
      "Cloud Computing & DevOps",
      "Advanced Security I & II",
    ],
  },
];

/* ── Tag colour map ── */
const TAG_COLOR = {
  MSc:      "tag-edu",
  BSc:      "tag-edu",
  Security: "tag-cyber",
  Cloud:    "tag-cloud",
};

/* ── Component ─────────────────────────────────────────────────── */
export default function ExperienceTimeline() {
  const [expanded, setExpanded] = useState(null);

  const toggle = (id) =>
    setExpanded((prev) => (prev === id ? null : id));

  return (
    <div className="exp-wrapper">
      {/* Horizontal rail — desktop */}
      <div className="exp-rail">
        <div className="exp-line" aria-hidden="true" />

        {TIMELINE.map((item, idx) => {
          const isOpen = expanded === item.id;

          return (
            <div
              key={item.id}
              className={`exp-node ${isOpen ? "open" : ""}`}
              style={{ "--idx": idx }}
            >
              {/* Dot */}
              <div className="exp-dot-wrap">
                <div className={`exp-dot ${item.type}`}>
                  <img src={item.logo} alt={item.org} className="exp-dot-logo" />
                </div>
              </div>

              {/* Card */}
              <div className="exp-card glass-exp">
                <div className="exp-card-head">
                  <span className={`exp-tag ${TAG_COLOR[item.tag] || "tag-edu"}`}>
                    {item.tag}
                  </span>
                  <h3 className="exp-title">{item.title}</h3>
                  <p className="exp-org">{item.org}</p>

                  <div className="exp-meta">
                    <span>
                      <FiCalendar size={11} /> {item.period}
                    </span>
                    <span>
                      <FiMapPin size={11} /> {item.location}
                    </span>
                  </div>

                  <p className="exp-preview">{item.preview}</p>
                </div>

                {/* Expandable details */}
                <div className={`exp-details ${isOpen ? "is-open" : ""}`}>
                  <ul className="exp-bullets">
                    {item.bullets.map((b, i) => (
                      <li key={i}>
                        <FiArrowRight size={12} className="bullet-icon" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  className="exp-toggle"
                  onClick={() => toggle(item.id)}
                  aria-expanded={isOpen}
                >
                  {isOpen ? (
                    <>Less <FiChevronUp size={14} /></>
                  ) : (
                    <>Read more <FiChevronDown size={14} /></>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}